import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import TextField from "../textField"
import localStorageService from "../../services/localStorage.service"
import authService from "../../services/auth.service"
import { useDispatch } from "react-redux"
import { loginUser } from "../../store/authSlice"
import dictionary from "../../utils/dictionary"
import { useSelector } from "react-redux"
import userService from "../../services/user.service"
import { Link, useLocation } from "react-router-dom"
import useRecaptchaToken from "../../hooks/useRecaptchaToken"
import { prefetchCrmData } from "../../hooks/useApiQueries"

function getPhoneValidationError(phone) {
  if (!phone || phone === "+") return null
  if (phone.startsWith("+3")) {
    if (!/^\+3\d{10}$/.test(phone) && !/^\+3\d{11}$/.test(phone)) {
      return "phone must contain 11-12 digits"
    }
    return null
  }
  if (phone.startsWith("+7")) {
    if (!/^\+7\d{10}$/.test(phone)) {
      return "phone must contain 11 digits"
    }
    return null
  }
  if (phone.length > 1) {
    return "phone must start with '7' or '3'"
  }
  return null
}

function isPhoneComplete(phone) {
  if (!phone || phone.length < 4) return false
  return !getPhoneValidationError(phone)
}

function formatLoginError(error, lang) {
  const detail = error?.response?.data?.detail
  const message =
    (typeof detail === "object" && detail?.message) ||
    (typeof detail === "string" ? detail : null) ||
    error?.message

  if (message?.includes("reCAPTCHA")) {
    return lang === "ru"
      ? "Не удалось проверить reCAPTCHA. Обновите страницу или отключите блокировщик рекламы."
      : message
  }
  if (error?.response?.status === 401) {
    return lang === "ru" ? "Неверный телефон или пароль" : "Invalid phone or password"
  }
  return (
    message ||
    (lang === "ru" ? "Не удалось войти. Попробуйте ещё раз." : "Login failed")
  )
}

const UserLoginForm = () => {
  const userId = localStorageService.getUserId()
  const [user, setUser] = useState(null)
  const loadData = async (id) => {
    setUser(await userService.getUserById(id))
  }
  useEffect(() => {
    if (userId) {
      loadData(userId)
    }
  }, [userId])

  const selectedLanguage = useSelector((state) => state.lang.lang)
  const t = dictionary[selectedLanguage]
  const navigate = useNavigate()
  const location = useLocation()
  const registerSuccess = location.state?.registerSuccess
  const [data, setData] = useState({
    phone: "+",
    password: "",
  })
  const [submitError, setSubmitError] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { required: recaptchaRequired, ready: recaptchaReady, getToken } =
    useRecaptchaToken()

  const phoneError = getPhoneValidationError(data.phone)
  const phoneComplete = isPhoneComplete(data.phone)
  const hasStoredSession = Boolean(localStorageService.getUserAccessToken())
  const crmAccessDenied =
    hasStoredSession &&
    user &&
    !user.isStaff &&
    !user.isCalendarAdmin &&
    !user.isPlatformAdmin

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitError("")

    if (!phoneComplete) {
      setSubmitError(
        selectedLanguage === "ru"
          ? "Введите полный номер телефона (+7… или +381…)"
          : "Enter a complete phone number"
      )
      return
    }

    if (recaptchaRequired && !recaptchaReady) {
      setSubmitError(
        selectedLanguage === "ru"
          ? "Загружается защита от ботов. Подождите пару секунд и нажмите снова."
          : "Security check is loading. Please wait and try again."
      )
      return
    }

    setIsSubmitting(true)
    try {
      const recaptchaToken = recaptchaRequired
        ? await getToken("user_login")
        : undefined
      const authData = await authService.userLogin(
        data.phone,
        data.password,
        recaptchaToken
      )
      if (authData) {
        localStorageService.setUserTokens({ ...authData })
        await prefetchCrmData(queryClient, authData.userId)
        dispatch(loginUser())
        navigate("/crm")
      }
    } catch (error) {
      console.error(error)
      setSubmitError(formatLoginError(error, selectedLanguage))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (target) => {
    setSubmitError("")
    if (target.name === "phone") {
      if (!data.phone.startsWith("+")) {
        setData((prevState) => ({
          ...prevState,
          [target.name]: "+" + target.value,
        }))
      } else {
        setData((prevState) => ({
          ...prevState,
          [target.name]: target.value,
        }))
      }
    } else {
      setData((prevState) => ({
        ...prevState,
        [target.name]: target.value,
      }))
    }
  }

  const handleClearSession = () => {
    localStorageService.removeUserAuthData()
    setUser(null)
    setSubmitError("")
  }

  const submitDisabled =
    isSubmitting || Boolean(phoneError) || !phoneComplete || !data.password

  return (
    <form onSubmit={handleSubmit}>
      <p className="text-brown mb-[4px] text-2xl max-md:text-lg">{t.login}</p>
      {registerSuccess ? (
        <p className="mb-4 text-sm text-brown" role="status">
          {t.crmRegisterPending}
        </p>
      ) : null}
      {crmAccessDenied ? (
        <div className="mb-4">
          <p className="text-red text-sm">
            {selectedLanguage === "ru"
              ? "У этого аккаунта нет доступа к CRM. Обратитесь к администратору или войдите под другим номером."
              : "This account has no CRM access. Contact admin or sign in with another phone."}
          </p>
          <button
            type="button"
            className="mt-2 text-sm text-brown underline hover:opacity-80"
            onClick={handleClearSession}
          >
            {selectedLanguage === "ru" ? "Выйти и войти заново" : "Sign out and try again"}
          </button>
        </div>
      ) : (
        <p className="text-lightBrown mb-[24px] text-sm drop-shadow-2xl">
          {t.phoneNumberWithPassword}
        </p>
      )}
      <TextField
        name="phone"
        label={t.phoneNumber}
        placeholder="+1234567890"
        type="text"
        value={data.phone}
        onChange={handleChange}
        error={phoneError}
        disabled={crmAccessDenied}
      />
      <TextField
        name="password"
        label={t.password}
        placeholder={t.enterPassword}
        type="password"
        value={data.password}
        onChange={handleChange}
        disabled={crmAccessDenied}
      />
      {submitError ? (
        <p className="mt-3 text-sm text-red" role="alert">
          {submitError}
        </p>
      ) : null}
      {recaptchaRequired && !recaptchaReady && !crmAccessDenied ? (
        <p className="mt-2 text-xs text-lightBrown">
          {selectedLanguage === "ru"
            ? "Загрузка проверки безопасности…"
            : "Loading security check…"}
        </p>
      ) : null}
      <button
        className="bg-brown rounded-lg text-white text-center w-full py-[8px] mt-[24px] hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
        type="submit"
        disabled={submitDisabled || crmAccessDenied}
      >
        {isSubmitting
          ? selectedLanguage === "ru"
            ? "Вход…"
            : "Signing in…"
          : t.login}
      </button>
      <div className="mt-3 flex flex-col gap-1 text-sm text-brown">
        <Link to="/crm/register" className="hover:opacity-80">
          {t.crmRegisterLink}
        </Link>
        <Link to="/crm/password-recovery" className="hover:opacity-80">
          {t.passwordRecovery}
        </Link>
      </div>
    </form>
  )
}

export default UserLoginForm
